import { FC, ReactNode } from 'react';
import { formatConfidence } from 'utils/number';
import styles from './suggestion-tile.module.scss';

/** One identity row in the suggestion tile's `<dl>`. `value` is any node — the
 *  consumer is responsible for its own empty fallback (e.g. an em dash). */
export interface SuggestionTileField {
  label: string;
  value: ReactNode;
}

interface Props {
  /** Eyebrow line, e.g. "AI Suggestion 1" (often with an inline Recommended tag). */
  eyebrow: ReactNode;
  /** Right-aligned status in the header (e.g. a "Correct" / "Confirmed" badge). */
  headerStatus?: ReactNode;
  /** Card title — typically the candidate name, optionally wrapped in a Link. */
  title: ReactNode;
  /** Identity rows rendered as a `<dl>`. */
  fields: SuggestionTileField[];
  /** Confidence as a 0–1 fraction, rendered as a percentage. Null/undefined hides the row. */
  confidenceScore?: number | null;
  /** Slot for the domain's confidence-breakdown table. */
  breakdown?: ReactNode;
  /**
   * Wraps the rendered body in the consumer's container (RadioTile, Tile, …).
   * Defaults to a plain `<div>`. The shell owns the INNER layout only —
   * selection mechanics and state tints live with the consumer's container.
   */
  container?: (body: ReactNode) => ReactNode;
}

// Generic AI-suggestion card shell. Used by the AFE Well match-review cards and
// available to any future suggestion surface (parties, pre-drill target zones)
// so the shared anatomy + styling can't drift across features.
export const SuggestionTile: FC<Props> = ({
  eyebrow,
  headerStatus,
  title,
  fields,
  confidenceScore,
  breakdown,
  container,
}) => {
  const body = (
    <>
      <div className={styles.header}>
        <span className={styles.eyebrow}>{eyebrow}</span>
        {headerStatus}
      </div>

      <p className={styles.name}>{title}</p>

      <dl className={styles.fields}>
        {fields.map((field) => (
          <div key={field.label} className={styles.field}>
            <dt>{field.label}</dt>
            <dd>{field.value}</dd>
          </div>
        ))}
      </dl>

      {((confidenceScore !== null && confidenceScore !== undefined) ||
        breakdown) && (
        <div className={styles.scoreSection}>
          <hr className={styles.divider} />
          {confidenceScore !== null && confidenceScore !== undefined && (
            <div className={styles.confidence}>
              <span className={styles.confidenceLabel}>Confidence</span>
              <span className={styles.confidenceValue}>
                {formatConfidence(confidenceScore)}
              </span>
            </div>
          )}
          {breakdown && (
            <>
              <hr className={styles.divider} />
              <p className={styles.breakdownLabel}>Confidence Breakdown</p>
              {breakdown}
            </>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      {container ? container(body) : <div className={styles.tile}>{body}</div>}
    </>
  );
};
